import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { Loader2, Mail, CheckCircle2, ArrowRight } from "lucide-react";

export default function Signup() {
  const { t } = useI18n();
  const { setUser, refresh } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", mobile: "", password: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [stage, setStage] = useState("form"); // form | verify
  const [otp, setOtp] = useState("");
  const [verifyError, setVerifyError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      setUser(data);
      if (data.email && !data.email_verified) {
        setStage("verify");
      } else {
        nav("/dashboard");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setVerifyError("");
    setVerifying(true);
    try {
      const { data } = await api.post("/auth/email/verify-otp", { code: otp.trim() });
      setUser(data);
      nav("/dashboard");
    } catch (err) {
      setVerifyError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setVerifying(false);
    }
  };

  const resend = async () => {
    setResendMsg("");
    setVerifyError("");
    setResending(true);
    try {
      const { data } = await api.post("/auth/email/send-otp");
      if (data.dev_otp) {
        setResendMsg(`Code sent. (Dev OTP: ${data.dev_otp})`);
      } else {
        setResendMsg("New code sent. Check your inbox.");
      }
    } catch (err) {
      setVerifyError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setResending(false);
    }
  };

  const skip = async () => {
    // Refresh user from /auth/me so the dashboard reflects latest state, then leave.
    try { await refresh(); } catch { /* noop */ }
    nav("/dashboard");
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center py-10 bg-slate-50/40" data-testid="signup-page">
      <div className="bg-white rounded-2xl border border-slate-200 nc-soft-shadow p-8 w-full max-w-md">
        <div className="w-12 h-12 rounded-xl nc-gradient mx-auto flex items-center justify-center text-white font-bold text-xl">N</div>

        {stage === "form" && (
          <>
            <h1 className="mt-4 text-center text-2xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>{t("auth.signupTitle")}</h1>
            <p className="text-center text-sm text-slate-500 mt-1">{t("auth.signupSub")}</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{t("auth.name")}</label>
                <input className="nc-input mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="signup-name-input" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{t("auth.mobile")}</label>
                <input className="nc-input mt-1" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} required maxLength="10" pattern="[0-9]{10}" data-testid="signup-mobile-input" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{t("auth.email")}</label>
                <input type="email" className="nc-input mt-1" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="signup-email-input" />
                <p className="text-xs text-slate-400 mt-1">If you add an email, we'll send a 6-digit code to verify it.</p>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{t("auth.password")}</label>
                <input type="password" className="nc-input mt-1" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength="8" data-testid="signup-password-input" />
              </div>
              {error && <p className="text-sm text-rose-600" data-testid="signup-error">{error}</p>}
              <button type="submit" disabled={loading} className="nc-btn-primary w-full justify-center" data-testid="signup-submit-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}{t("auth.signupCta")}
              </button>
            </form>
            <div className="mt-5 text-center text-sm text-slate-600">
              {t("auth.hasAccount")} <Link to="/login" className="text-emerald-700 font-medium" data-testid="goto-login-link">{t("auth.loginCta")}</Link>
            </div>
          </>
        )}

        {stage === "verify" && (
          <div data-testid="signup-verify-stage">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 text-emerald-700 mx-auto mt-3 flex items-center justify-center">
              <Mail className="w-6 h-6" />
            </div>
            <h2 className="mt-3 text-center text-2xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>Verify your email</h2>
            <p className="text-center text-sm text-slate-500 mt-1">
              We sent a 6-digit code to <strong className="text-slate-900">{form.email}</strong>. Enter it below to confirm.
            </p>
            <form onSubmit={verify} className="mt-6 space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Verification code</label>
                <input
                  className="nc-input mt-1 tracking-[8px] text-center text-lg font-mono"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  required
                  maxLength={6}
                  inputMode="numeric"
                  autoFocus
                  data-testid="signup-otp-input"
                />
              </div>
              {verifyError && <p className="text-sm text-rose-600" data-testid="signup-verify-error">{verifyError}</p>}
              {resendMsg && <p className="text-sm text-emerald-700" data-testid="signup-resend-msg">{resendMsg}</p>}
              <button type="submit" disabled={verifying || otp.length !== 6} className="nc-btn-primary w-full justify-center" data-testid="signup-verify-btn">
                {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Verify email
              </button>
            </form>
            <div className="mt-4 flex flex-col gap-2 text-sm text-center">
              <button onClick={resend} disabled={resending} className="text-emerald-700 hover:text-emerald-800 font-medium disabled:opacity-50" data-testid="signup-resend-btn">
                {resending ? "Sending…" : "Resend code"}
              </button>
              <button onClick={skip} className="text-slate-500 hover:text-slate-700 inline-flex items-center gap-1 justify-center" data-testid="signup-skip-btn">
                Skip for now <ArrowRight className="w-3 h-3" />
              </button>
            </div>
            <p className="mt-4 text-xs text-slate-400 text-center">
              You can verify your email anytime from your dashboard profile.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
